import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { Badge, Button, Card, cx, Input, segItem, segTrack, Select } from "./ui.tsx";

/**
 * Looking at memory three ways, for the Memory pages (a person's own, team & company) and the admin's
 * Shared memory: Topics (one card per person, client or thing it knows about), a Map (one topic and
 * what comes up with it, laid out in fixed rings so it never jumps around) and a List. Picking a topic
 * opens what is known about it on the right (below, on phones).
 *
 * Topics are the memory engine's entities. It doesn't say which are people and which are companies, so
 * cards are ordered by when they last came up.
 */

export type MemoryType = "world" | "experience" | "observation";

export interface MemItem {
  id: string;
  text: string;
  type: MemoryType;
  at: string | null;
  topics: string[];
  chatId: string | null;
  editable: boolean;
  shared?: { promotionId: number; audience: "group" | "company"; groupName: string | null; text: string | null } | null;
  from?: string;
  sharedWith?: { everyone: boolean; groups: string[] };
  promotionId?: number | null;
  promotion?: { audience: "group" | "company"; groupId: string | null } | null;
  forgottenAt?: string | null;
}

export type Scope = "mine" | "team" | "admin";
type View = "topics" | "map" | "list";

export interface Topic {
  key: string;
  name: string;
  items: MemItem[];
  last: number;
  /** Mine: how many of its memories went to the person's group, and to everyone. */
  group: number;
  company: number;
}

const OTHER = "\u0000other";
const time = (i: MemItem) => (i.at ? new Date(i.at).getTime() : 0);

/** One topic per name (any case), newest first; memories about nothing in particular go last, as "Other". */
export function buildTopics(items: MemItem[]): Topic[] {
  const by = new Map<string, Topic>();
  for (const i of items) {
    for (const name of i.topics.length ? i.topics : [OTHER]) {
      const key = name === OTHER ? OTHER : name.toLowerCase();
      let t = by.get(key);
      if (!t) by.set(key, (t = { key, name: name === OTHER ? "Other memories" : name, items: [], last: 0, group: 0, company: 0 }));
      t.items.push(i);
      t.last = Math.max(t.last, time(i));
      if (i.shared?.audience === "group") t.group++;
      if (i.shared?.audience === "company") t.company++;
    }
  }
  for (const t of by.values()) t.items.sort((a, b) => time(b) - time(a));
  return [...by.values()].sort((a, b) => (a.key === OTHER ? 1 : b.key === OTHER ? -1 : b.last - a.last || b.items.length - a.items.length));
}

/** How often two topics come up in the same memory. Capped per memory so one long list can't dominate. */
function cooccurrence(items: MemItem[]): Map<string, number> {
  const co = new Map<string, number>();
  for (const i of items) {
    const keys = [...new Set(i.topics.map((t) => t.toLowerCase()))].slice(0, 8);
    for (let a = 0; a < keys.length; a++)
      for (let b = a + 1; b < keys.length; b++) {
        const k = keys[a]! < keys[b]! ? `${keys[a]}|${keys[b]}` : `${keys[b]}|${keys[a]}`;
        co.set(k, (co.get(k) ?? 0) + 1);
      }
  }
  return co;
}
const pairKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);

export const fmtDate = (at: string | number | null) => {
  if (!at) return "";
  const d = new Date(at);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", ...(d.getFullYear() !== new Date().getFullYear() && { year: "numeric" }) });
};

const TYPE_LABEL: Record<Scope, Record<MemoryType, string>> = {
  mine: { world: "Fact", experience: "Something you did", observation: "Summary" },
  team: { world: "Fact", experience: "Experience", observation: "Summary" },
  admin: { world: "Fact", experience: "Experience", observation: "Summary" },
};

/** Where a memory went (mine) or who can recall it (team, admin). */
export function SharePill({ item, scope }: { item: MemItem; scope: Scope }) {
  if (scope === "mine") {
    if (!item.shared) return <Badge>Private</Badge>;
    return item.shared.audience === "company" ? <Badge tone="good">Shared with everyone</Badge> : <Badge tone="accent">Shared with {item.shared.groupName ?? "your group"}</Badge>;
  }
  if (!item.sharedWith || item.sharedWith.everyone) return <Badge tone="good">Everyone</Badge>;
  return <Badge tone="accent">{scope === "team" ? "Your team only" : `Only ${item.sharedWith.groups.join(", ") || "a group"}`}</Badge>;
}

function TopicSharing({ t }: { t: Topic }) {
  if (!t.group && !t.company) return <Badge>Private</Badge>;
  return (
    <>
      {!!t.company && <Badge tone="good">{t.company} with everyone</Badge>}
      {!!t.group && <Badge tone="accent">{t.group} with your group</Badge>}
    </>
  );
}

/** A summary is the best one-line gist; otherwise the newest memory. */
const gistOf = (t: Topic) => (t.items.find((i) => i.type === "observation") ?? t.items[0])?.text ?? "";

interface ExplorerProps {
  items: MemItem[];
  scope: Scope;
  /** Views offered, the first being the default. */
  views?: View[];
  /** Forgotten in this visit: shown struck through, with Undo, until the page is reloaded. */
  gone?: Set<string>;
  /** Mine: correcting a memory's text in place. */
  onCorrect?: (item: MemItem, text: string) => Promise<unknown>;
  /** Buttons for one memory (Forget / Undo, Remove, Who can recall it…). */
  actions?: (item: MemItem) => ReactNode;
  /** Under the List view (e.g. a way to bring back forgotten memories). */
  listFooter?: ReactNode;
  /** Above the views (e.g. "Learned this week"); only on the Topics view. */
  intro?: (go: (topic: string) => void, edit: (item: MemItem) => void) => ReactNode;
}

export function MemoryExplorer({ items, scope, views = ["topics", "map", "list"], gone, onCorrect, actions, listFooter, intro }: ExplorerProps) {
  const [view, setView] = useState<View>(views[0]!);
  const [q, setQ] = useState("");
  const [days, setDays] = useState(0);
  const [sel, setSel] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [shown, setShown] = useState(60);
  const drawer = useRef<HTMLDivElement>(null);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const since = days ? Date.now() - days * 86_400_000 : 0;
    return items.filter((i) => (!since || time(i) >= since) && (!needle || `${i.text} ${i.topics.join(" ")} ${i.from ?? ""}`.toLowerCase().includes(needle)));
  }, [items, q, days]);
  const topics = useMemo(() => buildTopics(visible), [visible]);
  const co = useMemo(() => cooccurrence(visible), [visible]);
  const current = topics.find((t) => t.key === sel) ?? topics[0] ?? null;

  /** Picks a topic; on phones the details are below the list, so bring them into view. */
  const go = (key: string, toView?: View) => {
    setSel(key.toLowerCase());
    setEditing(null);
    if (toView) setView(toView);
    if (window.innerWidth < 1024) requestAnimationFrame(() => drawer.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };
  const edit = (item: MemItem) => {
    setSel((item.topics[0] ?? OTHER).toLowerCase());
    setEditing(item.id);
    if (view === "list") setView(views.includes("topics") ? "topics" : "map");
  };

  const label = { topics: "Topics", map: "Map", list: scope === "admin" ? "Table" : "List" };
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div role="tablist" aria-label="View" className={segTrack}>
          {views.map((v) => (
            <button
              key={v}
              role="tab"
              aria-selected={view === v}
              onClick={() => setView(v)}
              className={cx(segItem(view === v), "px-3.5 py-1.5 text-[13px]")}
            >
              {label[v]}
            </button>
          ))}
        </div>
        <Input
          value={q}
          onChange={(e) => (setQ(e.target.value), setShown(60))}
          placeholder={scope === "admin" ? "Search memories, topics, people…" : "Search people, clients, projects…"}
          aria-label="Search memories"
          className="order-3 basis-full sm:order-none sm:max-w-xs sm:flex-1 sm:basis-auto"
        />
        <Select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Time" className="w-auto">
          <option value={0}>All time</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </Select>
      </div>

      {view === "topics" && intro?.((t) => go(t), edit)}

      {!visible.length ? (
        <Card className="px-5 py-10 text-center text-sm text-[var(--muted)]">Nothing matches.</Card>
      ) : view === "list" ? (
        <MemoryList items={visible} scope={scope} gone={gone} actions={actions} onEdit={onCorrect ? edit : undefined} onTopic={(t) => go(t, views.includes("topics") ? "topics" : "map")} footer={listFooter} />
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="min-w-0">
            {view === "topics" ? (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {topics.slice(0, shown).map((t) => (
                  <button
                    key={t.key}
                    onClick={() => go(t.key)}
                    aria-pressed={current?.key === t.key}
                    className={cx(
                      "flex flex-col gap-1.5 rounded-xl border bg-[var(--panel)] p-3.5 text-left transition",
                      current?.key === t.key ? "border-[var(--color-accent)] ring-3 ring-[var(--color-accent)]/15" : "border-[var(--line)] hover:border-[var(--line-strong)]",
                    )}
                  >
                    <span className="font-medium">{t.name}</span>
                    <span className="line-clamp-2 text-[13px] text-[var(--muted)]">{gistOf(t)}</span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-[var(--muted)]">
                      {t.items.length} {t.items.length === 1 ? "memory" : "memories"} · {fmtDate(t.last)}
                      {scope === "mine" && <TopicSharing t={t} />}
                    </span>
                  </button>
                ))}
                {topics.length > shown && (
                  <Button className="sm:col-span-2 xl:col-span-3" onClick={() => setShown(shown + 60)}>
                    Show more topics ({topics.length - shown} more)
                  </Button>
                )}
              </div>
            ) : (
              current && <MemoryMap topics={topics} co={co} center={current} scope={scope} onPick={(k) => go(k)} />
            )}
          </div>
          <div ref={drawer} className="scroll-mt-4 lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-auto">
            {current && (
              <TopicDetails
                key={current.key}
                topic={current}
                topics={topics}
                co={co}
                scope={scope}
                gone={gone}
                editing={editing}
                setEditing={setEditing}
                onCorrect={onCorrect}
                actions={actions}
                onPick={(k) => go(k)}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}

/** What is known about one topic: every memory about it, newest first, with what can be done to each. */
function TopicDetails({
  topic: t,
  topics,
  co,
  scope,
  gone,
  editing,
  setEditing,
  onCorrect,
  actions,
  onPick,
}: {
  topic: Topic;
  topics: Topic[];
  co: Map<string, number>;
  scope: Scope;
  gone?: Set<string>;
  editing: string | null;
  setEditing: (id: string | null) => void;
  onCorrect?: (item: MemItem, text: string) => Promise<unknown>;
  actions?: (item: MemItem) => ReactNode;
  onPick: (key: string) => void;
}) {
  const related = topics
    .filter((o) => o.key !== t.key && o.key !== OTHER && (co.get(pairKey(o.key, t.key)) ?? 0) > 0)
    .sort((a, b) => (co.get(pairKey(b.key, t.key)) ?? 0) - (co.get(pairKey(a.key, t.key)) ?? 0))
    .slice(0, 10);
  const chat = t.items.find((i) => i.chatId)?.chatId;
  const summary = t.items.find((i) => i.type === "observation");
  return (
    <Card className="p-4">
      <h3 className="text-base font-semibold">{t.name}</h3>
      <p className="mb-3 text-xs text-[var(--muted)]">
        {t.items.length} {t.items.length === 1 ? "memory" : "memories"} · last came up {fmtDate(t.last)}
      </p>
      {summary && <p className="mb-3 rounded-lg bg-[var(--panel-2)] px-3 py-2 text-sm">{summary.text}</p>}
      {scope === "mine" && (
        <div className="mb-1 flex flex-wrap gap-1.5">
          <TopicSharing t={t} />
        </div>
      )}
      <h4 className="mt-4 mb-1 text-[12.5px] font-medium text-[var(--muted)]">{scope === "mine" ? "What it remembers" : "What's known"}</h4>
      <ul>
        {t.items.map((i) => (
          <li key={i.id} className="border-t border-[var(--line)] py-2.5">
            {editing === i.id && onCorrect ? (
              <CorrectForm item={i} onSave={onCorrect} onDone={() => setEditing(null)} />
            ) : (
              <MemoryLine item={i} scope={scope} gone={gone?.has(i.id)} actions={actions} onEdit={onCorrect && i.editable && !gone?.has(i.id) ? () => setEditing(i.id) : undefined} />
            )}
          </li>
        ))}
      </ul>
      {!!related.length && (
        <>
          <h4 className="mt-4 mb-2 text-[12.5px] font-medium text-[var(--muted)]">Comes up with</h4>
          <div className="flex flex-wrap gap-1.5">
            {related.map((r) => (
              <button key={r.key} onClick={() => onPick(r.key)} className="rounded-full border border-[var(--line)] px-2.5 py-0.5 text-xs hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]">
                {r.name}
              </button>
            ))}
          </div>
        </>
      )}
      {scope === "mine" && chat && (
        <p className="mt-4 text-xs text-[var(--muted)]">
          Learned in your chats.{" "}
          <Link to={`/chat/${chat}`} className="text-[var(--color-accent)] hover:underline">
            Open the latest one
          </Link>
        </p>
      )}
    </Card>
  );
}

function MemoryLine({ item: i, scope, gone, actions, onEdit, onTopic }: { item: MemItem; scope: Scope; gone?: boolean; actions?: (item: MemItem) => ReactNode; onEdit?: () => void; onTopic?: (t: string) => void }) {
  return (
    <>
      <div className={cx("text-sm", gone && "text-[var(--muted)] line-through")}>{i.text}</div>
      {scope === "mine" && i.shared?.text && i.shared.text !== i.text && !gone && <div className="mt-1 text-xs text-[var(--muted)]">Shared as: “{i.shared.text}”</div>}
      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[var(--muted)]">
        <span>
          {fmtDate(i.at)} · {TYPE_LABEL[scope][i.type]}
        </span>
        <SharePill item={i} scope={scope} />
        {i.from && <span>{i.from}</span>}
        {onTopic &&
          i.topics.slice(0, 4).map((t) => (
            <button key={t} onClick={() => onTopic(t)} className="text-[var(--color-accent)] hover:underline">
              {t}
            </button>
          ))}
        <span className="ml-auto flex flex-wrap items-center gap-3">
          {onEdit && (
            <button onClick={onEdit} className="text-[var(--color-accent)] hover:underline">
              Correct
            </button>
          )}
          {actions?.(i)}
        </span>
      </div>
    </>
  );
}

function CorrectForm({ item, onSave, onDone }: { item: MemItem; onSave: (item: MemItem, text: string) => Promise<unknown>; onDone: () => void }) {
  const [text, setText] = useState(item.text);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await onSave(item, text.trim());
          onDone();
        } catch (err) {
          setError(err instanceof Error ? err.message : String(err));
        } finally {
          setBusy(false);
        }
      }}
    >
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        autoFocus
        aria-label="Corrected memory"
        className="w-full resize-y rounded-lg border border-[var(--color-accent)] bg-[var(--panel)] px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[var(--color-accent)]/20"
      />
      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-[var(--muted)]">
        <Button type="submit" variant="primary" className="h-7 px-2.5 text-xs" disabled={busy || text.trim().length < 3 || text.trim() === item.text}>
          {busy ? "Saving…" : "Save"}
        </Button>
        <Button type="button" variant="ghost" className="h-7 px-2.5 text-xs" onClick={onDone}>
          Cancel
        </Button>
        <span>{item.shared ? "Your assistant re-learns from this. What was shared isn't changed; remove it under Shared by me if it's wrong." : "Your assistant re-learns from this."}</span>
      </div>
      {error && <p className="mt-1 text-xs text-red-700">{error}</p>}
    </form>
  );
}

/** Every memory as a list (phones, screen readers, and finding one thing). */
function MemoryList({ items, scope, gone, actions, onEdit, onTopic, footer }: { items: MemItem[]; scope: Scope; gone?: Set<string>; actions?: (item: MemItem) => ReactNode; onEdit?: (i: MemItem) => void; onTopic: (t: string) => void; footer?: ReactNode }) {
  const [shown, setShown] = useState(50);
  const sorted = useMemo(() => [...items].sort((a, b) => time(b) - time(a)), [items]);
  useEffect(() => setShown(50), [items]);
  return (
    <>
      <Card className="divide-y divide-[var(--line)]">
        {sorted.slice(0, shown).map((i) => (
          <div key={i.id} className="px-4 py-3">
            <MemoryLine item={i} scope={scope} gone={gone?.has(i.id)} actions={actions} onEdit={onEdit && i.editable && !gone?.has(i.id) ? () => onEdit(i) : undefined} onTopic={onTopic} />
          </div>
        ))}
        {sorted.length > shown && (
          <div className="px-4 py-3">
            <Button onClick={() => setShown(shown + 100)}>Show more ({sorted.length - shown} more)</Button>
          </div>
        )}
      </Card>
      {footer}
    </>
  );
}

/* ── The map ─────────────────────────────────────────────────────────────── */

const W = 760;
const H = 560;
const CX = W / 2;
const CY = H / 2;

/**
 * The chosen topic in the middle, what comes up with it most on an inner ring (at most 10), and what
 * comes up with those on an outer ring (at most 14), each fanned out beyond its inner topic. A fixed
 * layout rather than a physics simulation, so picking another topic re-centres without anything jumping.
 */
export function mapLayout(topics: Topic[], co: Map<string, number>, center: Topic) {
  const w = (a: string, b: string) => co.get(pairKey(a, b)) ?? 0;
  const named = topics.filter((t) => t.key !== OTHER);
  const ring1 = named.filter((t) => t.key !== center.key && w(center.key, t.key) > 0).sort((a, b) => w(center.key, b.key) - w(center.key, a.key)).slice(0, 10);
  const inner = new Set([center.key, ...ring1.map((t) => t.key)]);
  const ring2 = named
    .filter((t) => !inner.has(t.key) && ring1.some((r) => w(r.key, t.key) > 0))
    .sort((a, b) => Math.max(...ring1.map((r) => w(r.key, b.key))) - Math.max(...ring1.map((r) => w(r.key, a.key))))
    .slice(0, 14);
  const pos = new Map<string, [number, number]>([[center.key, [CX, CY]]]);
  ring1.forEach((t, i) => {
    const a = (i / Math.max(ring1.length, 1)) * 2 * Math.PI - Math.PI / 2;
    pos.set(t.key, [CX + 165 * Math.cos(a), CY + 150 * Math.sin(a)]);
  });
  const parentOf = new Map(ring2.map((t) => [t.key, ring1.reduce((best, r) => (w(r.key, t.key) > w(best.key, t.key) ? r : best), ring1[0]!).key]));
  for (const p of ring1) {
    const kids = ring2.filter((t) => parentOf.get(t.key) === p.key);
    const [px, py] = pos.get(p.key)!;
    const base = Math.atan2((py - CY) / 150, (px - CX) / 165);
    const step = Math.min(0.42, (1.6 * Math.PI) / Math.max(ring1.length, 1) / Math.max(kids.length, 1));
    kids.forEach((t, k) => {
      const a = base + (k - (kids.length - 1) / 2) * step;
      pos.set(t.key, [CX + 300 * Math.cos(a), CY + 228 * Math.sin(a)]);
    });
  }
  const edges: { a: string; b: string; w: number }[] = [
    ...ring1.map((t) => ({ a: center.key, b: t.key, w: w(center.key, t.key) })),
    ...ring2.map((t) => ({ a: parentOf.get(t.key)!, b: t.key, w: w(parentOf.get(t.key)!, t.key) })),
  ];
  for (let i = 0; i < ring1.length; i++)
    for (let j = i + 1; j < ring1.length; j++) {
      const x = w(ring1[i]!.key, ring1[j]!.key);
      if (x) edges.push({ a: ring1[i]!.key, b: ring1[j]!.key, w: x });
    }
  return { nodes: [center, ...ring1, ...ring2], pos, edges };
}

function MemoryMap({ topics, co, center, scope, onPick }: { topics: Topic[]; co: Map<string, number>; center: Topic; scope: Scope; onPick: (key: string) => void }) {
  const { nodes, pos, edges } = useMemo(() => mapLayout(topics, co, center), [topics, co, center]);
  const radius = (t: Topic) => 13 + Math.min(Math.sqrt(t.items.length) * 6, 30);
  const sharedAt = (t: Topic) => scope !== "mine" || t.group + t.company > 0;
  return (
    <Card className="p-2">
      <p className="px-2 pt-1 text-xs text-[var(--muted)]">
        What comes up with <b className="text-[var(--text)]">{center.name}</b>. Pick any circle to move there.
      </p>
      {nodes.length === 1 && <p className="px-2 pt-1 text-xs text-[var(--muted)]">Nothing else has come up together with it yet.</p>}
      <svg viewBox={`0 -10 ${W} ${H + 20}`} role="img" aria-label={`Map of what comes up with ${center.name}`} className="block h-auto w-full">
        {edges.map((e) => {
          const [x1, y1] = pos.get(e.a)!;
          const [x2, y2] = pos.get(e.b)!;
          const strong = e.a === center.key || e.b === center.key;
          return <line key={`${e.a}|${e.b}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={strong ? "#9cc6dc" : "#e3e1da"} strokeWidth={Math.min(1 + e.w * 1.4, 7)} strokeLinecap="round" />;
        })}
        {nodes.map((t) => {
          const [x, y] = pos.get(t.key)!;
          const c = t.key === center.key;
          const r = radius(t) * (c ? 1.25 : 1);
          const below = y >= CY || c;
          const shared = sharedAt(t);
          const name = t.name.length > 28 ? `${t.name.slice(0, 26)}…` : t.name;
          return (
            <g
              key={t.key}
              tabIndex={0}
              role="button"
              aria-label={`${t.name}, ${t.items.length} memories`}
              className="group cursor-pointer outline-none"
              onClick={() => onPick(t.key)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onPick(t.key))}
            >
              <circle
                cx={x}
                cy={y}
                r={r}
                fill="#ffffff"
                stroke={shared ? "#0f8a65" : "#aaa89f"}
                strokeWidth={shared ? 2.5 : 2}
                strokeDasharray={shared ? undefined : "4 3"}
                className={c ? "fill-[var(--color-accent)]" : "transition-colors group-hover:fill-[#eaf6fc] group-focus-visible:fill-[#eaf6fc]"}
              />
              <text x={x} y={y + 4} textAnchor="middle" fontSize={11} fontWeight={600} fill={c ? "#ffffff" : "#6b6a64"} className="pointer-events-none">
                {t.items.length}
              </text>
              <text
                x={x}
                y={below ? y + r + 15 : y - r - 7}
                textAnchor="middle"
                fontSize={c ? 14 : 12}
                fontWeight={c ? 650 : 450}
                fill="#171716"
                stroke="#ffffff"
                strokeWidth={4}
                paintOrder="stroke"
                className="pointer-events-none"
              >
                {name}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="flex flex-wrap gap-x-4 gap-y-1 px-2 pb-1 text-xs text-[var(--muted)]">
        {scope === "mine" && (
          <>
            <span className="inline-flex items-center gap-1.5">
              <i className="inline-block h-3 w-3 rounded-full border-[2.5px] border-[#0f8a65]" />
              Some of it is shared
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="inline-block h-3 w-3 rounded-full border-2 border-dashed border-[#aaa89f]" />
              Private to you
            </span>
          </>
        )}
        <span>Bigger circle = mentioned more · thicker line = come up together more</span>
      </div>
    </Card>
  );
}
